import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Onboarding from "@/pages/Onboarding";
import AppShell from "@/components/AppShell";
import Home from "@/pages/Home";
import Discover from "@/pages/Discover";
import Match from "@/pages/Match";
import Chats from "@/pages/Chats";
import ChatRoom from "@/pages/ChatRoom";
import Memories from "@/pages/Memories";
import Profile from "@/pages/Profile";
import Membership from "@/pages/Membership";
import ConnectionRoom from "@/pages/ConnectionRoom";
import PaymentReturn from "@/pages/PaymentReturn";
import Admin from "@/pages/Admin";
import Legal from "@/pages/Legal";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import NotFound from "@/pages/NotFound";
import EnforcementScreen from "@/components/EnforcementScreen";
import ConnectionStatus from "@/components/ConnectionStatus";

function FullScreen({ children }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#0A0908] bg-noise px-5 text-center">
      {children}
    </div>
  );
}

function Spinner({ label }) {
  return (
    <FullScreen>
      <div className="flex gap-2" aria-live="polite">
        <span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" />
        <span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" />
        <span className="dot h-2 w-2 rounded-full bg-[#D4AF37]" />
      </div>
      <p className="text-sm text-[#8C8578]">{label || "Checking your session…"}</p>
    </FullScreen>
  );
}

function RequireAuth({ needOnboarded, children }) {
  const { user, loading, logout } = useAuth();
  const [showRecovery, setShowRecovery] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!loading) { setShowRecovery(false); return; }
    const t = window.setTimeout(() => setShowRecovery(true), 8000);
    return () => window.clearTimeout(t);
  }, [loading]);

  if (loading) {
    return showRecovery ? (
      <FullScreen>
        <p className="font-display text-2xl text-[#F4F1EA]">We're having trouble checking your session</p>
        <p className="max-w-xs text-sm text-[#8C8578]">This can happen on a slow or unstable connection.</p>
        <div className="flex gap-3">
          <button onClick={() => window.location.reload()} className="rounded-full bg-[#D4AF37] px-6 py-3 text-sm font-semibold text-[#0A0908]" data-testid="guard-retry">Try again</button>
          <button onClick={async () => { await logout(); window.location.href = "/"; }} className="rounded-full border border-[#3D3428] px-6 py-3 text-sm text-[#C8C2B5]" data-testid="guard-signout">Sign out and try again</button>
        </div>
      </FullScreen>
    ) : <Spinner />;
  }

  if (!user) return <Navigate to="/login" replace />;
  const enf = user.enforcement;
  if (enf && (enf.state === "suspended" || enf.state === "banned")) {
    return <EnforcementScreen enforcement={enf} />;
  }
  if (needOnboarded && !user.onboarded) return <Navigate to="/onboarding" replace />;
  if (!needOnboarded && user.onboarded) return <Navigate to="/app/home" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/onboarding" element={<RequireAuth needOnboarded={false}><Onboarding /></RequireAuth>} />
      <Route path="/membership/return" element={<PaymentReturn />} />
      <Route path="/app" element={<RequireAuth needOnboarded><AppShell /></RequireAuth>}>
        <Route index element={<Navigate to="/app/home" replace />} />
        <Route path="home" element={<Home />} />
        <Route path="discover" element={<Discover />} />
        <Route path="match" element={<Match />} />
        <Route path="chats" element={<Chats />} />
        <Route path="chat/:characterId" element={<ChatRoom />} />
        <Route path="connection/:matchId" element={<ConnectionRoom />} />
        <Route path="memories" element={<Memories />} />
        <Route path="membership" element={<Membership />} />
        <Route path="profile" element={<Profile />} />
        <Route path="admin" element={<Admin />} />
      </Route>
      <Route path="/privacy" element={<Legal page="privacy" />} />
      <Route path="/terms" element={<Legal page="terms" />} />
      <Route path="/guidelines" element={<Legal page="guidelines" />} />
      <Route path="/safety" element={<Legal page="safety" />} />
      <Route path="/ai-disclosure" element={<Legal page="ai-disclosure" />} />
      <Route path="/refund" element={<Legal page="refund" />} />
      <Route path="/how-it-works" element={<Legal page="how-it-works" />} />
      <Route path="/account-deletion" element={<Legal page="account-deletion" />} />
      <Route path="/contact" element={<Legal page="contact" />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-background text-foreground">
      <AuthProvider>
        <BrowserRouter>
          <ConnectionStatus />
          <AppRouter />
          <Toaster position="top-center" theme="dark" />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
